import { create } from 'zustand';

export interface AuthenticatedUser {
  userId: string;
  tenantId: string;
  tenantSlug: string;
  fullNameAr: string;
  fullNameEn: string | null;
  roles: string[];
}

export interface LoginResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: AuthenticatedUser;
}

interface AuthState {
  accessToken: string | null;
  user: AuthenticatedUser | null;
  tenantSlug: string | null;
  setSession: (response: LoginResponse) => void;
  setTenantSlug: (slug: string | null) => void;
  clear: () => void;
}

// Access token lives in memory only — never in localStorage. The refresh
// token rides in the wheels_refresh HttpOnly cookie set by the Api, so a
// page reload drops the access token; ProtectedRoute then runs a silent
// refresh before deciding to bounce to /login. Only the tenant slug (not
// a secret — it's in the subdomain in production) is persisted so that
// boot-time refresh can resolve the tenant.
const TENANT_SLUG_KEY = 'wheels.tenantSlug';

function readStoredTenantSlug(): string | null {
  try {
    return localStorage.getItem(TENANT_SLUG_KEY);
  } catch {
    return null;
  }
}

function storeTenantSlug(slug: string | null): void {
  try {
    if (slug) {
      localStorage.setItem(TENANT_SLUG_KEY, slug);
    }
  } catch {
    // Storage unavailable (private mode) — refresh-on-reload degrades
    // gracefully to a normal login.
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  tenantSlug: readStoredTenantSlug(),
  setSession: (response) => {
    storeTenantSlug(response.user.tenantSlug);
    set({
      accessToken: response.accessToken,
      user: response.user,
      tenantSlug: response.user.tenantSlug,
    });
  },
  setTenantSlug: (slug) => {
    storeTenantSlug(slug);
    set({ tenantSlug: slug });
  },
  // Keep the stored slug on clear — it prefills the next login and lets a
  // later reload still attempt the cookie refresh for that tenant.
  clear: () => set({ accessToken: null, user: null }),
}));

export function selectIsAuthenticated(state: AuthState): boolean {
  return state.accessToken !== null && state.user !== null;
}
